import React, { useState } from 'react';

import GenericChart, { chartColors, DEFAULT_HEIGHT, exportConfig, fontFamily } from '~/GenericChart/GenericChart';
import { roundedHumanFormat } from '~/utils/Price/Price';
import { BarSeriesValue, useSortingContext } from '../store/sortingContext';
import { ButtonSort } from './ButtonSort';
import { ButtonAddData } from './ButtonAddData';
import { ERPApiResult } from '~/libs/infrastructure/schemas/erp';

interface ChartBarProps {
	colors?: string[];
	currency: string;
	parseErpResults: ERPApiResult;
	title?: string;
	onDataAddRequest?: () => void;
}

export function ChartBar({ colors, currency, parseErpResults, title, onDataAddRequest }: ChartBarProps) {
	const { sortBy, handleSortChange, sortCategories, sortSeries } = useSortingContext();
	const [plotWidth, setPlotWidth] = useState<number | undefined>(undefined);

	const series: BarSeriesValue[] = Object.keys(parseErpResults.allDataByCountry).map((key) => {
		return [key, Number(parseErpResults.cumulativeStats[key]?.revenue?.value) || 0];
	});

	const seriesData = sortSeries(series);
	const categories = sortCategories(series, parseErpResults.stats);

	const isEmpty = parseErpResults.stats.cumulativeRevenues.length === 0;

	return (
		<div className='relative' style={{ minHeight: `${DEFAULT_HEIGHT}px` }}>
			<ButtonSort sortBy={sortBy} onChange={handleSortChange} offsetLeft={plotWidth} />
			<GenericChart
				setPlotWidth={setPlotWidth}
				options={{
					chart: {
						type: 'column',
						spacing: [10, 10, 0, 8],
						height: DEFAULT_HEIGHT,
						style: { position: 'inherit' },
					},

					colors: colors || chartColors,

					series: [
						{
							type: 'column',
							name: `Price [${currency}]`,
							data: seriesData,
						},
					],

					plotOptions: {
						column: {
							borderWidth: 0,
						},
						line: {
							softThreshold: false,
						},
					},

					title: {
						text: title || '',
					},

					subtitle: {
						text: '',
					},

					xAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						lineWidth: 0,
						title: {
							text: 'Country',
							margin: 10,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
						type: 'category',
						categories,
						labels: {
							align: 'center',
							x: 0,
							style: {
								color: '#666',
								fontWeight: '500',
								fontSize: '11px',
								fontFamily,
							},
						},
					},
					yAxis: {
						crosshair: true,
						gridLineWidth: 1,
						gridLineColor: '#ccc',
						gridLineDashStyle: 'Dash',
						min: 0,
						softMax: 1000,
						title: {
							text: `4Y revenue [${currency}]`,
							margin: 0,
							x: -12,
							style: {
								color: '#333',
								fontWeight: '600',
								fontSize: '12px',
								fontFamily,
							},
						},
					},

					exporting: exportConfig,

					legend: {
						enabled: false,
						align: 'left',
						verticalAlign: 'top',
						borderWidth: 0,
					},

					tooltip: {
						shared: true,
						borderRadius: 0,
						borderColor: '#000',
						backgroundColor: 'rgba(255,255,255,1)',
						shadow: false,
						useHTML: true,
						padding: 0,
						headerFormat: '',

						formatter: function (): string {
							const point = this.points?.[0];
							return `<div style='background: #fff;padding: 0.33rem; border: 0.06rem solid #000; border-bottom_: none; width: calc(100% + 2px)' >
								<strong style="z-index: 999;display:block;font-size:14px;margin-bottom:6px;color: ${point?.color}">${
								parseErpResults.allDataByCountry[point?.x || '']?.[0]?.country.name || ''
							}</strong><strong style="display:block;margin-top:6px;font-size:12px;font-variant-numeric: tabular-nums;">${roundedHumanFormat(
								currency,
								String(point?.y)
							)}</strong>
							</div>`;
						},
						style: {
							color: '#000',
							fontSize: '12px',
							fontFamily,
						},
					},
				}}
			/>
			{isEmpty && (
				<div className='absolute left-0 bottom-0 flex items-center justify-center w-full h-full bg-white bg-opacity-70 text-center'>
					<div className='p-4 bg-white bg-opacity-100'>
						{onDataAddRequest && (
							<>
								<h3 className='mb-0.5 font-display text-base font-medium text-cPurple-600 tracking-wider'>
									Price input, volume &amp; growth
								</h3>
								<p className='mb-2 font-display text-label leading-normal text-gray-500 tracking-wide'>
									Add the volume & growth data to see the revenues bar chart.
								</p>
								<ButtonAddData onClick={onDataAddRequest} />
							</>
						)}
					</div>
				</div>
			)}
		</div>
	);
}
